/* @flow */
import createChainedFunction from 'utils/createChainedFunction';
import cx from 'classnames';
import invariant from 'invariant';
import focusNode from 'utils/focusNode';
import KeyConstants from 'constants/KeyConstants';
import pickTextareaElementProps from 'utils/pickTextareaElementProps';
import PropTypes from 'prop-types';
import * as React from 'react';
import ReactDOM from 'react-dom';

import './styles/Textarea.scss';

const BORDER_WIDTH = 2;

/**
 * ## Renders a styled [Textarea](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/textarea).
 *
 * ```example
 * <Field>
 *   <Label for="textarea-1" hidden>Label</Label>
 *   <Textarea
 *     autogrow
 *     id="textarea-1"
 *     placeholder="Textarea"
 *   />
 * </Field>
 * ```
 *
 * ```example
 * <Field>
 *   <Label for="textarea-2" hidden>Label</Label>
 *   <Textarea
 *     autoselect
 *     id="textarea-2"
 *     value="Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor."
 *   />
 * </Field>
 * ```
 */

class Textarea extends React.Component<$FlowFixMeProps> {
  static propTypes = {
    /** Native */
    autofocus: PropTypes.bool,
    autogrow: PropTypes.bool,
    autoselect: PropTypes.bool,
    /** Native */
    placeholder: PropTypes.string,
    /** Native */
    rows: PropTypes.number,
    /** Native */
    value: PropTypes.string,
    onHeightChange: PropTypes.func,
    onReturnKeyPress: PropTypes.func,
    onUpdate: PropTypes.func,
    uncontrolled: PropTypes.bool,
    defaultValue: PropTypes.string,
  };

  static defaultProps = {
    rows: 3,
  };

  componentDidMount() {
    if (this.props.autogrow) {
      this.recalculateSize();
      window.addEventListener('resize', this.recalculateSize, false);
    }

    if (this.props.autofocus) {
      // Prevents the cursor from being placed at the start of the text
      const { value } = this.getInputNode();
      this.getInputNode().value = '';
      focusNode(this.getInputNode());
      this.getInputNode().value = value;
    }

    if (this.props.autoselect) {
      this.select();
    }
  }

  componentDidUpdate() {
    if (this.props.autogrow) {
      this.recalculateSize();
    }
    if (this.props.onUpdate) {
      this.props.onUpdate();
    }
  }

  componentWillUnmount() {
    if (this.props.autogrow) {
      window.removeEventListener('resize', this.recalculateSize, false);
    }
  }

  getInputNode = () => {
    const node = ReactDOM.findDOMNode(this.refs.textarea);
    invariant(
      node instanceof window.HTMLElement,
      'TextArea.getInputNode findDOMNode returned non HTMLElement'
    );
    return node;
  };

  select = () => {
    this.getInputNode().select();
  };

  focus = () => {
    this.getInputNode().focus();
  };

  clear = () => {
    this.getInputNode().value = '';
  };

  handleChange = () => {
    if (this.props.autogrow) {
      this.recalculateSize();
    }
  };

  handleKeyDown = (event: SyntheticKeyboardEvent<>) => {
    if (this.props.onReturnKeyPress && event.keyCode === KeyConstants.ENTER) {
      if (this.props.onReturnKeyPress(event)) {
        event.stopPropagation();
      }
    }
  };

  height = null;

  recalculateSize = () => {
    const node = ReactDOM.findDOMNode(this);
    invariant(
      node instanceof window.HTMLElement,
      'Textarea.recalculateSize findDOMNode returned non HTMLElement'
    );
    node.style.height = 'auto';

    const height = node.scrollHeight + BORDER_WIDTH;
    node.style.height = `${height}px`;

    if (this.height !== height) {
      this.height = height;
      if (this.props.onHeightChange) {
        this.props.onHeightChange();
      }
    }
  };

  render() {
    const { children, className, onChange, placeholder, value, ...others } = this.props;
    let initialValue = '';

    if (value) {
      initialValue = value;
    } else if (children) {
      initialValue = children;
    }

    const props = pickTextareaElementProps(others);

    return (
      <textarea
        {...props}
        ref="textarea"
        placeholder={placeholder}
        onKeyDown={this.handleKeyDown}
        className={cx('Textarea', className)}
        value={this.props.uncontrolled ? undefined : initialValue}
        onChange={createChainedFunction(onChange, this.handleChange)}>
        {this.props.uncontrolled ? this.props.defaultValue : this.props.children}
      </textarea>
    );
  }
}

export default Textarea;
